/**
 * useActiveSession — TanStack Query hook that resolves a real backend session
 * for the Play (chat) page.
 *
 * On mount it calls `getOrCreateSession()` which either returns the most
 * recent active session or creates a fresh one. The result is cached with a
 * long staleTime so navigating away from Play and back does NOT create a
 * duplicate session.
 *
 * Usage:
 *   const { sessionId, isLoading, error } = useActiveSession();
 */

import { useQuery } from '@tanstack/react-query';
import { getOrCreateSession } from '@/api/services/sessions.service';
import type { CreateSessionResponse } from '@/api/services/sessions.service';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UseActiveSessionResult {
  readonly sessionId: string | null;
  readonly isLoading: boolean;
  readonly error: string | null;
}

// ---------------------------------------------------------------------------
// Query key — stable across the app lifetime.
// ---------------------------------------------------------------------------

export const ACTIVE_SESSION_QUERY_KEY = ['sessions', 'active'] as const;

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Generate an ephemeral local session ID when the backend is unavailable.
 * Sessions with this prefix are treated as local-only (no WS subscription).
 */
function generateEphemeralSession(): CreateSessionResponse {
  return {
    sessionId: `play-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    createdAt: new Date().toISOString(),
  };
}

/**
 * Resilient session resolver: tries the backend first, falls back to an
 * ephemeral local session on any error so the chat surface always works.
 */
async function resolveSession(): Promise<CreateSessionResponse> {
  try {
    return await getOrCreateSession();
  } catch {
    // Backend sessions unavailable — fall back to an ephemeral local session
    return generateEphemeralSession();
  }
}

export function useActiveSession(): UseActiveSessionResult {
  const { data, isLoading, error } = useQuery({
    queryKey: ACTIVE_SESSION_QUERY_KEY,
    queryFn: resolveSession,
    // Cache the session for the entire app lifetime — navigating away from
    // Play and back should not create a new session. The user can explicitly
    // start a new conversation via a "New chat" action (future feature).
    staleTime: Infinity,
    // Do not refetch on window focus — sessions are long-lived.
    refetchOnWindowFocus: false,
    // Do not retry on failure — resolveSession already handles errors with a
    // fallback, so this should rarely fire.
    retry: false,
  });

  return {
    sessionId: data?.sessionId ?? null,
    isLoading,
    // With the fallback in place, errors should be extremely rare — but just
    // in case the fallback itself throws, handle gracefully.
    error: error
      ? 'Unable to start a session. Please try again.'
      : null,
  };
}
